'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';

type MonitorStatus = 'up' | 'down' | 'degraded' | 'unknown';

interface MonitorInfo {
  id: number;
  name: string;
  status: MonitorStatus;
}

interface StatusData {
  monitors: MonitorInfo[];
  overall: MonitorStatus;
}

const STATUS_UPTIME_URL = 'https://stats.uptimerobot.com/JVwO29f6ro';

const DOT: Record<MonitorStatus, string> = {
  up: 'bg-emerald-500',
  degraded: 'bg-amber-400',
  down: 'bg-rose-500',
  unknown: 'bg-neutral-600',
};

const PING: Record<MonitorStatus, string> = {
  up: 'bg-emerald-400',
  degraded: 'bg-amber-300',
  down: 'bg-rose-400',
  unknown: '',
};

const LABEL: Record<MonitorStatus, string> = {
  up: 'All systems operational',
  degraded: 'Partial degradation',
  down: 'Service disruption',
  unknown: 'Checking status…',
};

const LABEL_COLOR: Record<MonitorStatus, string> = {
  up: 'text-emerald-400',
  degraded: 'text-amber-400',
  down: 'text-rose-400',
  unknown: 'text-neutral-500',
};

export default function StatusBar() {
  const [data, setData] = useState<StatusData | null>(null);

  useEffect(() => {
    let cancelled = false;

    async function load() {
      try {
        const res = await fetch('/api/status');
        if (!res.ok) return;
        const json: StatusData = await res.json();
        if (!cancelled) setData(json);
      } catch {
        // fail silently
      }
    }

    void load();
    const id = setInterval(load, 60_000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  const overall = data?.overall ?? 'unknown';
  const monitors = data?.monitors ?? [];

  return (
    <Link
      href={STATUS_UPTIME_URL}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex flex-wrap items-center gap-x-5 gap-y-2 px-4 py-2 rounded-lg border border-white/5 bg-white/[0.02] hover:bg-white/[0.04] hover:border-white/10 transition group"
      title="System status - powered by UptimeRobot"
    >
      {/* Overall indicator */}
      <span className="inline-flex items-center gap-2">
        <span className="relative flex h-2 w-2" aria-hidden="true">
          {PING[overall] && (
            <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-75 ${PING[overall]}`} />
          )}
          <span className={`relative inline-flex rounded-full h-2 w-2 ${DOT[overall]}`} />
        </span>
        <span className={`text-xs font-medium ${LABEL_COLOR[overall]}`}>
          {LABEL[overall]}
        </span>
      </span>

      {/* Per-service dots */}
      {monitors.length > 0 && (
        <span className="inline-flex items-center gap-3">
          {monitors.map((m) => (
            <span key={m.id} className="inline-flex items-center gap-1.5" title={m.name}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${DOT[m.status]}`} aria-hidden="true" />
              <span className="text-[11px] text-neutral-500 group-hover:text-neutral-400 transition">{m.name}</span>
            </span>
          ))}
        </span>
      )}
    </Link>
  );
}
